import { cn } from "@/lib/utils"

/**
 * Horizontal stacked bar for AR ageing. Expects five buckets coming back
 * from the firm-report endpoint: ``current``, ``1_30``, ``31_60``,
 * ``61_90``, ``over_90``. Bucket "current" covers not-yet-due balances.
 *
 * Renders with accessible labels and a legend below. Degrades gracefully
 * to text on mobile.
 */

export interface AgeingBucket {
  count: number
  amount: number | string
}

export interface AgeingBarProps {
  buckets: {
    current?: AgeingBucket
    "1_30"?: AgeingBucket
    "31_60"?: AgeingBucket
    "61_90"?: AgeingBucket
    over_90?: AgeingBucket
  }
  formatAmount: (v: number) => string
}

const ORDER: Array<keyof AgeingBarProps["buckets"]> = [
  "current",
  "1_30",
  "31_60",
  "61_90",
  "over_90",
]

const LABELS: Record<string, string> = {
  current: "Not due",
  "1_30": "1–30 days",
  "31_60": "31–60 days",
  "61_90": "61–90 days",
  over_90: "90+ days",
}

const COLORS: Record<string, string> = {
  current: "bg-emerald-500",
  "1_30": "bg-yellow-400",
  "31_60": "bg-orange-500",
  "61_90": "bg-rose-500",
  over_90: "bg-red-700",
}

function amountNumber(b?: AgeingBucket): number {
  if (!b) return 0
  const v = typeof b.amount === "string" ? parseFloat(b.amount) : b.amount
  return Number.isFinite(v) ? v : 0
}

export function AgeingBar({ buckets, formatAmount }: AgeingBarProps) {
  const total = ORDER.reduce((acc, k) => acc + amountNumber(buckets[k]), 0)

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "flex h-3 w-full overflow-hidden rounded-full bg-muted",
          total === 0 && "opacity-40",
        )}
        role="img"
        aria-label="Accounts receivable ageing distribution"
      >
        {ORDER.map((k) => {
          const amt = amountNumber(buckets[k])
          const pct = total > 0 ? (amt / total) * 100 : 0
          if (pct <= 0) return null
          return (
            <div
              key={k as string}
              className={cn("h-full", COLORS[k as string])}
              style={{ width: `${pct}%` }}
              title={`${LABELS[k as string]}: ${formatAmount(amt)}`}
            />
          )
        })}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-5">
        {ORDER.map((k) => {
          const b = buckets[k]
          const amt = amountNumber(b)
          return (
            <div key={k as string} className="min-w-0">
              <dt className="flex items-center gap-1.5 text-muted-foreground">
                <span
                  className={cn("h-2 w-2 shrink-0 rounded-full", COLORS[k as string])}
                  aria-hidden
                />
                <span className="truncate">{LABELS[k as string]}</span>
              </dt>
              <dd className="mt-0.5 font-medium tabular-nums">{formatAmount(amt)}</dd>
              {b ? (
                <dd className="text-muted-foreground">
                  {b.count} {b.count === 1 ? "invoice" : "invoices"}
                </dd>
              ) : null}
            </div>
          )
        })}
      </dl>
    </div>
  )
}
